/**
 * The Filing status of one client-month (D-13, FR-E38), and the filing preference (D-11).
 *
 * Read from `GET /filing/{client}/months/{period}`: whether the lane is on and, when anything
 * stands in its way, what is owed (credentials, version pins, the edge header, step-up); the
 * session; every submission sent to the portal (D-07's, none on this build); every portal call
 * this month made — each in sentences, with the one action it offers: *Check again* on a call
 * still waiting on the portal, *Try again* on one that did not complete. Nothing retries on its
 * own. Nothing here has run against GSTN.
 */

import { Alert, Button, Card } from '@fiveox/ui'
import { useCallback, useEffect, useState } from 'react'

import { CLIENT, FILING, PERIOD, useEvaluation } from '../lib/access'
import {
  type FilingMonth as FilingMonthBody,
  type PortalPreference,
  filing,
  periodFiling,
} from '../lib/api'
import { istWords, refusalWords } from '../lib/filingLane'
import { QUARTERS, fetchLine, financialYearOf, laneOff } from '../lib/portalFetch'

export function FilingMonth({ clientId }: { clientId: string }) {
  const { can } = useEvaluation()
  const [period, setPeriod] = useState('')
  const [month, setMonth] = useState<FilingMonthBody | null>(null)
  const [preference, setPreference] = useState<PortalPreference | null>(null)
  const [chosen, setChosen] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // A month, or a quarterly (QRMP) client's quarter — the service refuses a quarter for a
  // client it holds monthly, in words.
  const valid = /^(\d{2}-\d{4}|Q[1-4]-\d{4}-\d{2})$/.test(period)
  const fy = valid ? financialYearOf(period) : null

  const load = useCallback(async () => {
    if (!valid) {
      setMonth(null)
      return
    }
    try {
      setMonth((await filing.month(clientId, period)).data)
    } catch (e) {
      setError(refusalWords(e))
    }
  }, [clientId, period, valid])

  useEffect(() => {
    void load()
  }, [load])

  async function act(run: () => Promise<unknown>) {
    setBusy(true)
    setError(null)
    try {
      await run()
    } catch (e) {
      setError(refusalWords(e))
    } finally {
      setBusy(false)
      void load()
    }
  }

  const off = month ? laneOff(month.lane) : null

  return (
    <Card className="p-4">
      <p className="gst-panel-h mb-2">Filing status for a month</p>
      <label className="gst-label mb-3">
        Month
        <input
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          placeholder="07-2026"
          title="A month, or a quarterly client's quarter (Q2-2026-27)"
          className="gst-input w-32"
        />
      </label>
      {error && <Alert tone="error">{error}</Alert>}
      {!month ? (
        <p className="gst-sub">Type a month (MM-YYYY) to see what was sent to and read from the portal.</p>
      ) : (
        <div className="space-y-4">
          {off && <p>{off}</p>}
          {month.lane.owed.length > 0 && (
            <div>
              <p className="gst-meta mb-1">
                {off ? 'What is owed before the portal can be used:' : 'Still owed before every call can run:'}
              </p>
              <ul className="list-disc pl-5">
                {month.lane.owed.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <p className="gst-meta mb-1">Session</p>
            <p>
              {month.session.words}
              {month.session.expires_at && ` It ends at ${istWords(month.session.expires_at)}.`}
            </p>
          </div>

          <div>
            <p className="gst-meta mb-1">Returns sent to the portal</p>
            <p>{month.submissions_sentence}</p>
            {month.submissions.length > 0 && (
              <ul className="space-y-1">
                {month.submissions.map((s) => (
                  <li key={s.id}>
                    {s.return_words} for {s.period_words}: {s.action_words} — {s.state_words}.{' '}
                    {s.sentence}
                    {s.arn && ` Acknowledgement number ${s.arn}.`}
                    {s.next?.action === 'check_submission' && (
                      <>
                        {' '}
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={busy || !!off}
                          onClick={() => void act(() => periodFiling.check(s.period_id, s.id))}
                        >
                          {s.next.label}
                        </Button>
                      </>
                    )}
                    {s.errors.length > 0 && (
                      <>
                        <p className="gst-meta">The portal’s error report:</p>
                        <ul className="list-disc pl-5">
                          {s.errors.map((e) => (
                            <li key={e}>{e}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <p className="gst-meta mb-1">Read from and changed on the portal</p>
            {month.fetches.length === 0 ? (
              <p>Nothing has been read from the portal for this month.</p>
            ) : (
              <ul className="space-y-2">
                {month.fetches.map((f) => (
                  <li key={f.id} className="flex flex-wrap items-center gap-2">
                    <span>{fetchLine(f)}</span>
                    {f.next && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={busy || !!off}
                        onClick={() =>
                          void act(() =>
                            f.next?.action === 'check'
                              ? filing.check(clientId, f.id)
                              : filing.retry(clientId, f.id),
                          )
                        }
                      >
                        {f.next.label}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {can(PERIOD, 'can_update') && (
            <div>
              <p className="gst-meta mb-1">Ledger balances</p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy || !!off}
                onClick={() => void act(() => filing.readLedgers(clientId, period))}
              >
                Read the ledger balances from the portal
              </Button>
              <p className="gst-meta">
                The GSTR-3B uses the portal’s balances first; a balance typed for the month is
                kept beside them and any difference is named.
              </p>
            </div>
          )}

          {fy && (
            <div>
              <p className="gst-meta mb-1">Filing preference for {fy}</p>
              {can(PERIOD, 'can_update') && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={busy || !!off}
                  onClick={() =>
                    void act(async () => setPreference((await filing.readPreference(clientId, fy)).data))
                  }
                >
                  Read the preference from the portal
                </Button>
              )}
              {off && <p className="gst-meta">{off}</p>}
              {preference && (
                <ul className="mt-2 list-disc pl-5">
                  {preference.words.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              )}
              {can(CLIENT, 'can_update') && can(FILING, 'can_update') && (
                <form
                  className="mt-2 flex flex-wrap items-end gap-2"
                  onSubmit={(e) => {
                    e.preventDefault()
                    void act(() => filing.savePreference(clientId, fy, chosen))
                  }}
                >
                  {QUARTERS.map((q) => (
                    <label key={q} className="gst-label">
                      {q}
                      <select
                        className="gst-select"
                        value={chosen[q] ?? ''}
                        onChange={(e) => {
                          const value = e.target.value
                          setChosen((was) => {
                            const next = { ...was }
                            if (value) next[q] = value
                            else delete next[q]
                            return next
                          })
                        }}
                      >
                        <option value="">Leave as it is</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                      </select>
                    </label>
                  ))}
                  <Button
                    type="submit"
                    size="sm"
                    disabled={busy || !!off || Object.keys(chosen).length === 0}
                  >
                    Save the preference on the portal
                  </Button>
                </form>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  )
}
